/**
 * Home Audit API functions
 * API calls for managing home safety audit sessions and questions
 */

import { apiClient } from '@/core/api/client';
import type {
  SessionResponseDto,
  CreateSessionDto,
  SaveResponsesDto,
  AuditQuestion,
} from '../types/homeAudit.types';

const BASE_URL = '/home-audit';

type RawActionItem = Omit<import('../types/homeAudit.types').ActionItem, 'title' | 'description' | 'isDiy'> & {
  recTitle: string;
  recDescription: string;
  isDIY: boolean;
};

type RawSessionResponse = Omit<SessionResponseDto, 'actionItems'> & {
  actionItems: RawActionItem[];
};

const normalizeSession = (raw: RawSessionResponse): SessionResponseDto => ({
  ...raw,
  actionItems: raw.actionItems.map(({ recTitle, recDescription, isDIY, ...rest }) => ({
    ...rest,
    title: recTitle,
    description: recDescription,
    isDiy: isDIY,
  })),
});

type CreateSessionResponse = {
  session: SessionResponseDto;
  created: boolean;
};

export type LatestCompletedSummary = {
  id: string;
  completedAt: string | null;
  compositeScore: number | null;
};

/**
 * Create a new session or return the existing in-progress session (idempotent per user)
 * Backend: POST /home-audit/sessions — returns { session, created }
 * We unwrap and return only the SessionResponseDto.
 */
export const createOrResume = async (
  stairsNa = false,
): Promise<SessionResponseDto> => {
  const body: CreateSessionDto = { stairsNa };
  const response = await apiClient.post<CreateSessionResponse>(
    `${BASE_URL}/sessions`,
    body,
  );
  return response.data.session;
};

/**
 * Get the active (IN_PROGRESS) session for the authenticated user.
 * Returns null on HTTP 404 (no active session). Re-throws all other errors.
 * Backend: GET /home-audit/sessions/active
 *
 * Note: the Axios response interceptor (handleResponseError) transforms 4xx/5xx
 * responses into plain objects { message, statusCode, errors } before this catch
 * block runs — so we must check statusCode, not err.response?.status.
 */
export const getActiveSession = async (): Promise<SessionResponseDto | null> => {
  try {
    const response = await apiClient.get<SessionResponseDto>(
      `${BASE_URL}/sessions/active`,
    );
    return response.data;
  } catch (err: unknown) {
    const statusCode = (err as { statusCode?: number }).statusCode;
    if (statusCode === 404) return null;
    throw err;
  }
};

/**
 * Save a batch of responses for a single room (per-room flush, D-04)
 * Backend: POST /home-audit/sessions/:id/responses -> 204
 */
export const saveResponses = async (
  sessionId: string,
  dto: SaveResponsesDto,
): Promise<void> => {
  await apiClient.post(`${BASE_URL}/sessions/${sessionId}/responses`, dto);
};

/**
 * Complete a session — triggers server-side scoring snapshot (SCR-04)
 * Backend: POST /home-audit/sessions/:id/complete
 */
export const complete = async (sessionId: string): Promise<SessionResponseDto> => {
  const response = await apiClient.post<RawSessionResponse>(
    `${BASE_URL}/sessions/${sessionId}/complete`,
  );
  return normalizeSession(response.data);
};

/**
 * Fetch a session by id (for completed sessions or resume by id)
 * Backend: GET /home-audit/sessions/:id
 */
export const getSession = async (
  sessionId: string,
): Promise<SessionResponseDto> => {
  const response = await apiClient.get<RawSessionResponse>(
    `${BASE_URL}/sessions/${sessionId}`,
  );
  return normalizeSession(response.data);
};

/**
 * Get the most recent COMPLETED session summary (id, completedAt, compositeScore).
 * Returns null on 404 (no completed sessions). Used by AuditEntryPage options screen.
 * Backend: GET /home-audit/sessions/latest-completed
 */
export const getLatestCompletedSession = async (): Promise<LatestCompletedSummary | null> => {
  try {
    const response = await apiClient.get<LatestCompletedSummary>(
      `${BASE_URL}/sessions/latest-completed`,
    );
    return response.data;
  } catch (err: unknown) {
    const statusCode = (err as { statusCode?: number }).statusCode;
    if (statusCode === 404) return null;
    throw err;
  }
};

/**
 * Fetch the full question catalog from the backend static data
 * Backend: GET /home-audit/questions
 */
export const getQuestions = async (): Promise<AuditQuestion[]> => {
  const response = await apiClient.get<AuditQuestion[]>(`${BASE_URL}/questions`);
  return response.data;
};
